import { useCallback, useContext, useMemo } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { DataContext } from '../context/DataContext';
import { WHISPERBI_SECURITY_API } from '../env';

const API_BASE_URL = WHISPERBI_SECURITY_API;
const INVITES_URL = `${API_BASE_URL}/invites`;
const ENROLL_URL = `${API_BASE_URL}/enroll`;

async function getIdToken() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    return tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('UserInviteService.getIdToken: unable to fetch auth session', error);
    return null;
  }
}

async function request(url, options = {}, { requireAuth = true } = {}) {
  const init = { ...options };
  const headers = new Headers(init.headers || {});

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (requireAuth) {
    const token = await getIdToken();
    if (!token) {
      throw new Error('No se encontró un token de autenticación para realizar la operación solicitada.');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  init.headers = headers;
  init.mode = init.mode || 'cors';
  init.cache = init.cache || 'no-cache';

  const response = await fetch(url, init);

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      const backendMessage = data?.message || data?.error || data?.errorMessage;
      if (backendMessage) {
        errorMessage = backendMessage;
      }
    } catch {
      const text = await response.text().catch(() => '');
      if (text) {
        errorMessage = text;
      }
    }
    throw new Error(errorMessage || 'Error inesperado al comunicar con el servicio de invitaciones.');
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

export function calculateExpiresInHours(expiresAtInput) {
  if (!expiresAtInput) {
    return undefined;
  }

  const expiresDate = new Date(expiresAtInput);
  if (Number.isNaN(expiresDate.getTime())) {
    return undefined;
  }

  const diffMs = expiresDate.getTime() - Date.now();
  if (diffMs <= 0) {
    return 0;
  }

  return Math.ceil(diffMs / (1000 * 60 * 60));
}

// Devuelve { items, nextCursor }. `startKey` era el LastEvaluatedKey en
// JSON plano, que pierde el tipo de las claves numéricas; el backend lo
// sigue aceptando un ciclo pero `cursor` es el que hay que usar.
export async function listInvites({ status, limit, cursor, startKey } = {}) {
  const params = new URLSearchParams();
  if (status) {
    params.set('status', status);
  }
  if (limit) {
    params.set('limit', String(limit));
  }
  if (cursor) {
    params.set('cursor', cursor);
  } else if (startKey) {
    params.set('start_key', typeof startKey === 'string' ? startKey : JSON.stringify(startKey));
  }

  const url = params.toString() ? `${INVITES_URL}?${params.toString()}` : INVITES_URL;
  const response = await request(url, { method: 'GET' });
  return {
    items: Array.isArray(response?.items) ? response.items : [],
    nextCursor: response?.next_cursor || null
  };
}

export async function createInvite({ email, role, expiresAt, claims } = {}) {
  if (!email || !role) {
    throw new Error('El correo y el rol son obligatorios para crear una invitación.');
  }

  const payload = {
    email: email.trim(),
    role
  };

  const expiresInHours = calculateExpiresInHours(expiresAt);
  if (typeof expiresInHours === 'number' && expiresInHours > 0) {
    payload.expires_in_hours = expiresInHours;
  }

  if (claims && typeof claims === 'object') {
    payload.claims = claims;
  }

  return request(INVITES_URL, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function resendInvite(inviteId) {
  if (!inviteId) {
    throw new Error('Se requiere el identificador de la invitación para reenviarla.');
  }
  const url = `${INVITES_URL}/${encodeURIComponent(inviteId)}/resend`;
  return request(url, { method: 'POST' });
}

export async function revokeInvite(inviteId) {
  if (!inviteId) {
    throw new Error('Se requiere el identificador de la invitación para revocarla.');
  }
  const url = `${INVITES_URL}/${encodeURIComponent(inviteId)}/revoke`;
  return request(url, { method: 'POST' });
}

export async function validateInviteToken(inviteToken) {
  if (!inviteToken) {
    throw new Error('Falta el token de invitación para validar el enrolamiento.');
  }
  const url = `${ENROLL_URL}/${encodeURIComponent(inviteToken)}`;
  return request(url, { method: 'GET' }, { requireAuth: false });
}

export async function completeEnroll({ inviteToken, metadata } = {}) {
  if (!inviteToken) {
    throw new Error('Falta el token de invitación.');
  }

  const payload = {
    invite_token: inviteToken,
    metadata
  };

  // Requiere sesión: el cliente ya debe haber hecho signUp()+confirmSignUp()
  // +autoSignIn() antes de llegar acá (ver Enroll.jsx) -- el backend deriva
  // la identidad del JWT (claims.sub/email), ya no confía en email/user_id
  // del body.
  return request(`${ENROLL_URL}/complete`, {
    method: 'POST',
    body: JSON.stringify(payload)
  }, { requireAuth: true });
}

function ensureInviteManagerAccess(isPlatformRoot, isTenantAdmin) {
  if (!isPlatformRoot && !isTenantAdmin) {
    throw new Error('Solo administradores de la cuenta pueden realizar esta operación.');
  }
}

export const useUserInviteService = () => {
  const { isPlatformRoot, isTenantAdmin } = useContext(DataContext);

  const guard = useCallback(() => {
    ensureInviteManagerAccess(isPlatformRoot, isTenantAdmin);
  }, [isPlatformRoot, isTenantAdmin]);

  const rootActions = useMemo(() => ({
    listInvites: async (params) => {
      guard();
      return listInvites(params);
    },
    createInvite: async (payload) => {
      guard();
      return createInvite(payload);
    },
    resendInvite: async (inviteId) => {
      guard();
      return resendInvite(inviteId);
    },
    revokeInvite: async (inviteId) => {
      guard();
      return revokeInvite(inviteId);
    }
  }), [guard]);

  return {
    ...rootActions,
    validateInviteToken,
    completeEnroll
  };
};

const UserInviteService = {
  listInvites,
  createInvite,
  resendInvite,
  revokeInvite,
  validateInviteToken,
  completeEnroll,
  calculateExpiresInHours,
  useUserInviteService
};

export default UserInviteService;
